import { Briefcase, TrendingUp } from "lucide-react";
import { Card } from "../ui";
import type { Money } from "./money";
import { acronym, eur, eurShort, Flag, Kpi, Row, tagMatches, type Asset, type Company } from "./shared";

export function CompaniesTab({ assets, companies, money, inflowsLoaded }: { assets: Asset[]; companies: Company[]; money: Money | null; inflowsLoaded: boolean }) {
  const y = money?.yearRow;
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {money && y && (
        <Card className="lg:col-span-2" title={<>{y.year} across the group <span className="muted ml-1 text-xs font-normal">from the CRM Finance module</span></>} icon={<TrendingUp size={16} />}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Sales" value={eurShort(y.sales)} />
            <Kpi label="Profit" value={eurShort(money.profit)} sub={eur(money.profit)} />
            <Kpi label={`Expected dividend, Dec${money.tax ? ` (net of ${money.tax}%)` : ""}`} value={eurShort(money.dividendNet)} sub={money.tax ? `${eur(money.profit)} before tax` : "set the tax in Wealth settings"} />
            <Kpi label="Cash received so far" value={eurShort(y.cashIn)} sub={`of ${eurShort(y.sales)} sales`} />
          </div>
          {y.byCompany?.Unassigned && (
            <div className="mt-2">
              <Flag pill="unassigned">{eur(y.byCompany.Unassigned.profit)} of this year's profit has no company set in the CRM — fill in the company on those project rows and it moves to the right card.</Flag>
            </div>
          )}
        </Card>
      )}
      {companies.length === 0 && <Card className="lg:col-span-2"><p className="muted text-sm">No companies listed yet. Add them in Wealth settings (the button at the top of this page).</p></Card>}
      {companies.map((c) => {
        const lines = assets.filter((a) => a.category === "cash" && tagMatches(a.location, c.name));
        const cash = lines.reduce((s, a) => s + a.value, 0);
        const byCompany = money?.yearRow?.byCompany || {};
        const lc = c.name.toLowerCase();
        const key = Object.keys(byCompany).find((k) => k.toLowerCase() === lc || lc.includes(k.toLowerCase()) || (c.accounts || "").toLowerCase().includes(k.toLowerCase()) || (acronym(c.name).length >= 2 && k.toUpperCase() === acronym(c.name)));
        const row = key ? byCompany[key] : null;
        const tax = money?.tax || 0;
        return (
          <Card key={c.id || c.name} title={<>{c.name} <span className="muted ml-1 text-xs font-normal">100% yours</span></>} icon={<Briefcase size={16} />}>
            <Row label="Cash now" value={eur(cash)} strong />
            {lines.map((a) => <Row key={a.id} indent label={a.name} value={eur(a.value)} muted />)}
            {!lines.length && <p className="muted py-1 text-sm">No cash line is tagged with "{c.name}" yet — set "Where it sits" on the account rows in Assets.</p>}
            {c.accounts && <p className="muted mt-2 text-xs">Accounts: {c.accounts}</p>}
            <div className="mt-2.5 border-t border-[var(--line)] pt-2">
              <Row muted label="Sales this year (CRM)" value={row ? eur(row.sales) : "—"} />
              <Row muted label="Profit this year (CRM)" value={row ? <span className="font-semibold text-[var(--text)]">{eur(row.profit)}</span> : "—"} />
              <Row muted label={`Dividend share, Dec${tax ? ` (net of ${tax}%)` : ""}`} value={row ? eur(row.profit * (1 - tax / 100)) : "—"} />
              <p className="muted mt-1.5 text-xs">
                {row ? `Matched to "${key}" in the CRM Finance module.` : inflowsLoaded ? `No CRM company matches "${c.name}" — name it IFT or IFI (or put that code in its accounts) to link the figures.` : "Reading the CRM…"}
              </p>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
